import graphql__59605e482b6b990ee9a4 from "./__generated__/MailingListCriterionFieldsAttributeDefinitionsQuery.graphql.ts";import * as React from "react";
import {fetchQuery, graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useFormikContext} from "formik";
import {useIntl} from "react-intl";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";

type AttributeDefinition = {
    id: string;
    name: string;
    label: string;
    type: string;
};

type CriterionFormValues = {
    type: string;
    attributeName: string;
    operator: string;
    value: string;
};

/**
 * Form fields for the membership criterion of a dynamic mailing list. Rendered inside a Formik
 * form; only shown when the list type is DYNAMIC. Version 1 supports a single condition with the
 * operators EQUALS and EXISTS, matching a contact attribute chosen from the attribute definitions.
 */
const MailingListCriterionFields: React.FC = () => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {values, setFieldValue} = useFormikContext<CriterionFormValues>();
    const [attributeDefinitions, setAttributeDefinitions] = React.useState<AttributeDefinition[]>([]);

    React.useEffect(() => {
        fetchQuery(relayEnvironment, graphql__59605e482b6b990ee9a4, {}).toPromise().then((data) => {
            //@ts-ignore
            const nodes = data?.attributeDefinitions?.edges?.map((edge) => edge.node) || [];
            setAttributeDefinitions(
                nodes.filter((node) => (node.entityTypes || []).includes('contact'))
            );
        });
    }, [relayEnvironment]);

    const selectedDefinition = attributeDefinitions.find((definition) => definition.name === values.attributeName);
    const showValueField = values.operator === 'EQUALS';
    const isBoolean = selectedDefinition?.type === 'BOOLEAN';

    // A boolean value field renders a <select> whose first option ("true") is shown by default, but
    // Formik's value stays empty until the user actively changes the selection. Pre-fill it so the
    // displayed option matches the form state and the "required" validation does not falsely trigger.
    React.useEffect(() => {
        if (values.type === 'DYNAMIC' && showValueField && isBoolean
            && values.value !== 'true' && values.value !== 'false') {
            setFieldValue('value', 'true');
        }
    }, [values.type, showValueField, isBoolean, values.value, setFieldValue]);

    if (values.type !== 'DYNAMIC') {
        return null;
    }

    return (
        <>
            <Row>
                <Label>{intl.formatMessage({id: 'dialog.CreateMailingListDialog.attributeLabel'})}</Label>
                <Field name="attributeName" as="select">
                    <option value="">{intl.formatMessage({id: 'dialog.CreateMailingListDialog.attributePlaceholder'})}</option>
                    {attributeDefinitions.map((definition) => (
                        <option key={definition.id} value={definition.name}>
                            {definition.label} ({definition.name})
                        </option>
                    ))}
                </Field>
            </Row>
            <Row>
                <Label>{intl.formatMessage({id: 'dialog.CreateMailingListDialog.operatorLabel'})}</Label>
                <Field name="operator" as="select">
                    <option value="EQUALS">{intl.formatMessage({id: 'mailingList.criterion.operator.equals'})}</option>
                    <option value="EXISTS">{intl.formatMessage({id: 'mailingList.criterion.operator.exists'})}</option>
                </Field>
            </Row>
            {showValueField && (
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.CreateMailingListDialog.valueLabel'})}</Label>
                    {isBoolean ? (
                        <Field name="value" as="select">
                            <option value="true">{intl.formatMessage({id: 'mailingList.criterion.value.true'})}</option>
                            <option value="false">{intl.formatMessage({id: 'mailingList.criterion.value.false'})}</option>
                        </Field>
                    ) : (
                        <Field name="value" type="text" placeholder={intl.formatMessage({id: 'dialog.CreateMailingListDialog.valuePlaceholder'})}/>
                    )}
                </Row>
            )}
        </>
    );
};

export default MailingListCriterionFields;
